import { getRouteApi, useNavigate } from '@tanstack/react-router';

import { FormattedMessage, useIntl } from 'react-intl';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import BackIcon from '@mui/icons-material/ArrowBack';
import Typography from '@mui/material/Typography';

import Spacings from '~/modules/ui-kit/spacings';
import generalMessages from '~/containers/App/messages';

import ContentView from '~/components/ContentView';
import CircuitForm from '~/components/CircuitForm';

import { useCircuit } from '~/hooks/api/useCircuit';
import { useEditCircuit } from '~/hooks/api/useEditCircuit';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import messages from './messages';
import styles from './styles.module.css';

const route = getRouteApi('/circuits/$circuitId');

const CircuitEdit = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const params = route.useParams();
  const search = route.useSearch();
  const parsedCircuitId = parseInt(params.circuitId, 10);
  const goBackToCircuitList = () => {
    navigate({ to: search.from || '/circuits' });
  };
  const { data, isLoading } = useCircuit(parsedCircuitId);
  const editCircuitMutation = useEditCircuit(parsedCircuitId, {
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.saveSuccess),
      });
      goBackToCircuitList();
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.saveFailure),
      });
    },
  });

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar className={styles.header}>
            <Spacings.Inline alignItems="center">
              <IconButton
                color="inherit"
                onClick={goBackToCircuitList}
                size="large"
              >
                <BackIcon data-testid="back" />
              </IconButton>
              <div>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'inherit',
                  }}
                >
                  <FormattedMessage {...messages.title} />
                </Typography>
              </div>
            </Spacings.Inline>
          </Toolbar>
        </AppBar>
      }
      isLoading={editCircuitMutation.isPending || isLoading}
    >
      {data && (
        <CircuitForm
          onSubmit={(values) =>
            editCircuitMutation.mutate({
              name: values.name,
              language: values.language,
              circuitOverseer: values.circuitOverseerName
                ? {
                    name: values.circuitOverseerName,
                    email: values.circuitOverseerEmail ?? '',
                  }
                : null,
            })
          }
          initialValues={{
            circuitOverseerEmail: data.circuit?.circuitOverseer?.email ?? '',
            circuitOverseerName: data.circuit?.circuitOverseer?.name ?? '',
            language: data.circuit?.language ?? '',
            name: data.circuit?.name ?? '',
          }}
        >
          <div>
            <Button
              variant="contained"
              disabled={editCircuitMutation.isPending}
              color="primary"
              type="submit"
            >
              {editCircuitMutation.isPending ? (
                <FormattedMessage {...generalMessages.loading} />
              ) : (
                <FormattedMessage {...messages.saveButton} />
              )}
            </Button>
          </div>
        </CircuitForm>
      )}
    </ContentView>
  );
};

export default CircuitEdit;
